import { Alert, Card, CodeViewer, Flex, Typography } from "djuno-design";

const { Title, Text } = Typography;

const FormOverviewPage = () => {
  return (
    <Flex direction="col" className="p-10">
      {/* Title and Description */}
      <Title level={2}>Form Overview</Title>

      <Text size="sm">
        To get started with <strong>react-hook-form</strong>, you first need to
        install the package using npm or yarn. Run the following command in your
        terminal:
      </Text>
      <CodeViewer
        copyable
        language="shell"
        code={`npm install react-hook-form`}
      />

      <Text size="sm" style={{ marginTop: 10 }}>
        Once installed, you can use the <strong>useForm</strong> hook to manage
        forms in React. This hook provides methods for handling input
        validation, submission, and error tracking, which simplifies form
        management.
      </Text>

      <Text size="sm">
        The basic usage of <strong>react-hook-form</strong> involves
        initializing the form with <code>useForm</code>, registering form
        elements such as <strong>Input</strong>, <strong>Checkbox</strong>,{" "}
        <strong>Switcher</strong>, <strong>Select</strong>, and{" "}
        <strong>Textarea</strong> components from the{" "}
        <strong>djuno-design</strong> package, and defining validation rules.
        Here is a detailed explanation:
      </Text>

      <Text size="sm" style={{ marginTop: 10 }}>
        <strong>Input:</strong> This component is used to capture user input,
        such as names or emails. You can set validation rules to ensure that the
        user provides the necessary information.
      </Text>

      <Text size="sm" style={{ marginTop: 10 }}>
        <strong>Checkbox:</strong> This component allows users to confirm their
        agreement to terms or conditions. You can manage its state and enforce
        requirements for the user to check the box before form submission.
      </Text>

      <Text size="sm" style={{ marginTop: 10 }}>
        <strong>Switcher:</strong> This toggle component can be utilized for
        binary choices, such as enabling or disabling options in the form. Its
        state can be dynamically updated based on user interaction.
      </Text>

      <Text size="sm" style={{ marginTop: 10 }}>
        <strong>Select:</strong> This component provides a dropdown menu for
        users to select from a predefined list of options. You can implement
        validation to ensure a selection is made before submission.
      </Text>

      <Text size="sm" style={{ marginTop: 10 }}>
        <strong>Textarea:</strong> This component is ideal for collecting longer
        text input, such as comments or notes. Validation can ensure that the
        field is filled out as needed.
      </Text>

      <Alert uiType="info" showIcon style={{ marginTop: 10 }}>
        <Text size="sm">
          For all components you can use a local <strong>state</strong> or
          Controller component of <strong>react-hook-form</strong> to controll
          your component
        </Text>
      </Alert>

      {/* Code Viewer Example */}
      <Card title="React Hook Form with djuno-design Example" className="mt-10">
        <CodeViewer
          language="javascript"
          copyable
          code={`import React, { useState } from "react";
import { Flex, Button, Input, Textarea, Checkbox, Switcher, Select, Typography } from "djuno-design"; // Importing components from djuno-design
import { useForm } from "react-hook-form"; // Import useForm from react-hook-form

const { Title, Text } = Typography;

const FormOverviewPage = () => {
  const { register, handleSubmit, formState: { errors }, control } = useForm();
  const [isChecked, setIsChecked] = useState(false); // State for checkbox
  const [selectedChain, setSelectedChain] = useState(null); // State for selected chain
  const networksLoading = false; // Placeholder for loading state (should be managed appropriately)

  // Handle form submission
  const onSubmit = (data) => {
    console.log("Form Data: ", data); // Log form data for debugging
  };

  return (
    <Flex direction="col" className="p-10">
      <Title level={2}>Form Overview</Title>
      <Text className="text-sm mb-5">
        This form allows users to input their details and comments. All fields are validated before submission to ensure data integrity.
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Input */}
        <Input
          label="What's their name?"
          { ...register("name", { required: "Name is required." }) }
          error={errors.name?.message}
        />
        
        {/* Checkbox */}
        <Checkbox
          label="Checkbox required form"
          { ...register("terms") }
           error={errors.terms.message}
        />
        
        {/* Select (by local state) */}
        <Select
          options={groupedNetworks.map((gn) => ({
            label: capitalizeFirstLetter(gn.ChainName),
            value: gn.ChainName,
          }))}
          value={selectedChain?.ChainName}
          onChange={setSelectedChain}
          error={errors.chain.message}
        />

        {/* Select (by react-hook-form controller) */}
        <Controller
          control={control}
          name="chain"
          render={({ field: { value, onChange } }) => (
            <Select
              value={value}
              onChange={onChange}
              options={Options}
            />
          )}
        />

        {/* Switcher (by local state) */}
        <Switcher
          onChange={(value) => setSelectedChain(value)} // Example function for toggling
          value={selectedChain === "mini"} // Adjust based on your logic
        />

        {/* Switcher (by react-hook-form controller) */}
        <Controller
          control={control}
          name="selected-chain"
          render={({ field: { value, onChange } }) => (
            <Switcher
              onChange={onChange}
              value={value}
            />
          )}
        />

        {/* Textarea for Comments */}
        <Textarea
          label="Textarea"
          placeholder="Enter custom notes if any"
          hint="Djuno Design"
          {...register("comments", { required: "Comments are required." })}
        />
        {errors.comments && <Text color="red">{errors.comments.message}</Text>}

        {/* Submit Button */}
        <Button type="submit" uiType="primary" style={{ marginTop: 20 }}>
          Submit
        </Button>
      </form>
    </Flex>
  );
};

export default FormOverviewPage;
`}
        />
      </Card>
    </Flex>
  );
};

export default FormOverviewPage;
